import React, { useRef, useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const VideoPlayerModal = ({ isOpen, onClose, videoUrl, title = "Tutorial Video" }) => {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const [animationStage, setAnimationStage] = useState('closed'); // 'closed', 'opening', 'open'
  const [videoError, setVideoError] = useState(false);

  // Validate video URL
  const isValidVideoURL = (url) => {
    if (!url || typeof url !== 'string') return false;
    try {
      new URL(url);
      // Basic check for video file extensions or known video hosting patterns
      return url.match(/\.(mp4|webm|ogg|mov|avi|mkv)(\?.*)?$/i) || 
             url.includes('youtube.com') || 
             url.includes('vimeo.com') ||
             url.includes('youtu.be') ||
             url.includes('tinyurl.com');
    } catch {
      return false;
    }
  };

  // Handle animation when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setAnimationStage('opening');
      // Start the opening animation sequence
      const timer = setTimeout(() => {
        setAnimationStage('open');
      }, 100);
      
      return () => clearTimeout(timer);
    } else {
      setAnimationStage('closed');
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle video auto-play and pause
  useEffect(() => {
    if (isOpen && videoRef.current && animationStage === 'open') {
      const playTimer = setTimeout(() => {
        videoRef.current.play().catch(error => {
          console.log('Auto-play was prevented by browser policy:', error);
        });
      }, 800);

      return () => clearTimeout(playTimer);
    } else if (!isOpen && videoRef.current) {
      videoRef.current.pause();
    }
  }, [isOpen, animationStage]);

  // Reset error state when URL changes
  useEffect(() => {
    setVideoError(false);
  }, [videoUrl]);

  if (!isOpen) return null;

  // Show error if invalid URL
  if (!isValidVideoURL(videoUrl)) {
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}
        onClick={onClose}
      >
        <div 
          style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
            width: '90%',
            maxWidth: '500px',
            padding: '24px',
            textAlign: 'center'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ margin: '0 0 16px 0', color: '#f9fafb', fontSize: '18px' }}>
            Invalid Video URL
          </h2>
          <p style={{ margin: '0 0 24px 0', color: '#d1d5db', fontSize: '14px', lineHeight: '1.5' }}>
            The tutorial video URL is not configured or invalid. Please contact an administrator to set up the tutorial video URL in the settings.
          </p>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Calculate animation styles based on stage
  const getHeaderStyle = () => {
    const baseStyle = {
      padding: '16px 24px',
      borderBottom: '1px solid #374151',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#374151',
      transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      zIndex: 10
    };

    if (animationStage === 'opening') {
      return {
        ...baseStyle,
        // Start at center of screen, move up to final position
        transform: 'translateY(calc(50vh - 30px))', // 30px is roughly half the header height
        opacity: 0.8
      };
    } else if (animationStage === 'open') {
      return {
        ...baseStyle,
        transform: 'translateY(0)',
        opacity: 1
      };
    }
    return baseStyle;
  };

  const getFooterStyle = () => {
    const baseStyle = {
      padding: '16px 24px',
      backgroundColor: '#374151',
      borderTop: '1px solid #4b5563',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      zIndex: 10
    };

    if (animationStage === 'opening') {
      return {
        ...baseStyle,
        // Start at center of screen, move down to final position
        transform: 'translateY(calc(-50vh + 30px))', // 30px is roughly half the footer height
        opacity: 0.8
      };
    } else if (animationStage === 'open') {
      return {
        ...baseStyle,
        transform: 'translateY(0)',
        opacity: 1
      };
    }
    return baseStyle;
  };

  const getVideoAreaStyle = () => {
    const baseStyle = {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#000000',
      position: 'relative',
      transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden'
    };

    if (animationStage === 'opening') {
      return {
        ...baseStyle,
        // Start with zero height - the space between header and footer
        height: '0px',
        opacity: 0,
        transform: 'scaleY(0)'
      };
    } else if (animationStage === 'open') {
      return {
        ...baseStyle,
        minHeight: '400px',
        opacity: 1,
        transform: 'scaleY(1)'
      };
    }
    return baseStyle;
  };

  const getModalStyle = () => {
    const baseStyle = {
      backgroundColor: '#1f2937',
      borderRadius: '12px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
      width: '90%',
      maxWidth: '1200px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'all 0.4s ease-out'
    };

    if (animationStage === 'opening') {
      return {
        ...baseStyle,
        transform: 'scale(0.9)',
        opacity: 0.7
      };
    } else if (animationStage === 'open') {
      return {
        ...baseStyle,
        transform: 'scale(1)',
        opacity: 1
      };
    }
    
    return baseStyle;
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: animationStage === 'opening' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
        transition: 'background-color 0.8s ease-out'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        style={getModalStyle()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - starts at screen center, moves up */}
        <div style={getHeaderStyle()}>
          <h2 style={{
            margin: 0,
            color: '#f9fafb',
            fontSize: '18px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            🎥 {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#d1d5db',
              fontSize: '24px',
              cursor: 'pointer',
              width: '32px',
              height: '32px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#4b5563';
              e.target.style.color = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#d1d5db';
            }}
            title="Close (ESC)"
          >
            ×
          </button>
        </div>

        {/* Video Player - grows from zero height as header/footer separate */}
        <div style={getVideoAreaStyle()}>
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            autoPlay={false}
            preload="metadata"
            muted={false}
            playsInline
            style={{
              width: '100%',
              height: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
              transition: 'all 0.8s ease-out',
              opacity: animationStage === 'open' ? 1 : 0,
              transform: animationStage === 'open' ? 'scale(1)' : 'scale(0.95)'
            }}
            onError={(e) => {
              console.error('Video failed to load:', e);
              setVideoError(true);
            }}
            onLoadStart={() => {
              console.log('Video loading started');
            }}
            onCanPlay={() => {
              console.log('Video can start playing');
            }}
            onPlay={() => {
              console.log('Video started playing');
            }}
          >
            Your browser does not support the video tag.
          </video>
          
          {/* Loading indicator */}
          {animationStage === 'opening' && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#9ca3af',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}>
              🎬 Opening curtains...
            </div>
          )}

          {/* Video error indicator */}
          {videoError && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#f87171',
              fontSize: '16px',
              textAlign: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #dc2626'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚠️</div>
              <div>Failed to load video</div>
              <div style={{ fontSize: '12px', marginTop: '8px', color: '#fca5a5' }}>
                Please check the video URL or try again later
              </div>
            </div>
          )}
        </div>

        {/* Footer - starts at screen center, moves down */}
        <div style={getFooterStyle()}>
          <div style={{
            fontSize: '12px',
            color: '#9ca3af'
          }}>
            Press ESC or click outside to close • Video auto-plays
          </div>
          
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center'
          }}>
            <button
              onClick={() => {
                if (videoRef.current) {
                  if (videoRef.current.paused) {
                    videoRef.current.play();
                  } else {
                    videoRef.current.pause();
                  }
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#3b82f6';
              }}
            >
              ⏯️ Play/Pause
            </button>
            
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#4b5563';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#6b7280';
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerModal;